import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Button } from "reactstrap";
import { GENERATING_REPORT } from "../../../constants/loading.constant";
import { useAnalyticsCore } from "../../../cores/analytics";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import {
  collectSummaryReportSections,
  downloadPdfFromSections,
  setSummaryCaptureMode,
} from "../../utils/reportCapture";
import { UpgradePlanButton } from "../plan/UpgradePlanButton";

interface Props {
  disabled?: boolean;
}

export const SummaryReportActions = ({ disabled = false }: Props) => {
  const intl = useIntl();
  const { turnOnLoading, turnOffLoading } = useGlobalCore();
  const { company, companyPlan, updateReportsCompany } = useMeCore();
  const { getInsightReport } = useAnalyticsCore();

  const canDownload = useMemo(() => {
    if (moment().isSame(moment(company?.lastReportDate), "M")) {
      return company?.reports! < companyPlan?.rules.reportsAmount!;
    }
    return true;
  }, [companyPlan, company]);

  const handleDownloadReport = useCallback(async () => {
    const root = document.getElementById("summaryReportRoot");
    if (!root || !company?.id) return;

    turnOnLoading(GENERATING_REPORT);

    try {
      await getInsightReport(company.id, async () => {
        await setSummaryCaptureMode(true);
        const sections = collectSummaryReportSections();

        if (!sections.length) {
          throw new Error("No report sections found");
        }

        await downloadPdfFromSections(sections, "wembii-resumen.pdf");
        updateReportsCompany(company.reports! + 1);
      });
    } finally {
      await setSummaryCaptureMode(false);
      turnOffLoading(GENERATING_REPORT, 500);
    }
  }, [
    company,
    getInsightReport,
    turnOffLoading,
    turnOnLoading,
    updateReportsCompany,
  ]);

  if (!canDownload) {
    return (
      <div className="summary-analytics__report-actions">
        <UpgradePlanButton />
      </div>
    );
  }

  return (
    <div className="summary-analytics__report-actions">
      <Button
        color="primary"
        className="summary-analytics__report-button"
        disabled={disabled}
        onClick={handleDownloadReport}
      >
        <FontAwesomeIcon icon={["far", "file-pdf"]} className="mr-2" />
        {intl.formatMessage({ id: "app.analytics.summary.downloadReport" })}
      </Button>
    </div>
  );
};
